'use client';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useOverwolfStore } from '../hooks/use-overwolf-store';
import { subscribeOverwolfStoreDownload } from '../services/overwolf/overwolf-store.service';

// How long to keep the in-progress indicator up (pinned at 100%) after a
// download completes, so the button doesn't cut abruptly back to its idle
// label the instant the installer file finishes.
const DOWNLOAD_COMPLETE_LINGER_MS = 3000;

// Progress of the currently-downloading Overwolf-Electron app, keyed by appId.
// The shell runs one download at a time, but keying by appId keeps consumers
// simple (each download button checks its own ids) and is robust if that ever
// changes. The value is the last reported progress percentage (0–100).
export interface ElectronDownloadContextValue {
  downloads: Record<string, number>;
}

export const ElectronDownloadContext =
  createContext<ElectronDownloadContextValue>({ downloads: {} });

// -----------------------------------------------------------------------------
// Mounted once near the app root. Subscribes to the shell's download lifecycle
// events (start / progress / complete / error) and exposes a per-app progress
// map so download buttons can show a loader while their app is downloading.
export const ElectronDownloadProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const { isAvailable: isOverwolfStoreAvailable } = useOverwolfStore();
  const [downloads, setDownloads] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!isOverwolfStoreAvailable) {
      return;
    }

    const dropDownload = (appId: string): void =>
      setDownloads((prev) => {
        if (!(appId in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[appId];
        return next;
      });

    return subscribeOverwolfStoreDownload((event) => {
      const { appId } = event;
      if (!appId) {
        return;
      }

      switch (event.event) {
        case 'download-start':
          setDownloads((prev) => ({ ...prev, [appId]: 0 }));
          break;
        case 'download-progress':
          setDownloads((prev) => ({
            ...prev,
            [appId]: Number(event.progress) || 0,
          }));
          break;
        // Pin at 100% and keep the indicator up a short beat before dropping it,
        // so the button doesn't cut back to its idle label the instant the
        // download finishes.
        case 'download-complete':
          setDownloads((prev) => ({ ...prev, [appId]: 100 }));
          setTimeout(() => dropDownload(appId), DOWNLOAD_COMPLETE_LINGER_MS);
          break;
        // A failed download is no longer in progress — drop it immediately.
        case 'download-error':
          dropDownload(appId);
          break;
      }
    });
  }, [isOverwolfStoreAvailable]);

  const value = useMemo<ElectronDownloadContextValue>(
    () => ({ downloads }),
    [downloads],
  );

  return (
    <ElectronDownloadContext.Provider value={value}>
      {children}
    </ElectronDownloadContext.Provider>
  );
};

// -----------------------------------------------------------------------------
// Whether any of the given app ids is currently downloading, plus its progress.
// Pass the specific download's id so only the clicked button lights up — the
// main button and each alternative download track their own id.
export const useElectronDownload = (
  appIds: string | string[],
): { isDownloading: boolean; progress: number } => {
  const { downloads } = useContext(ElectronDownloadContext);
  const ids = Array.isArray(appIds) ? appIds : [appIds];

  for (const id of ids) {
    if (id && id in downloads) {
      return { isDownloading: true, progress: downloads[id] };
    }
  }
  return { isDownloading: false, progress: 0 };
};

// -----------------------------------------------------------------------------
// Returns a predicate for checking a single app id — handy in lists (e.g. the
// multi-download menu) where each row tracks its own download independently.
export const useIsAppDownloading = (): ((appId: string) => boolean) => {
  const { downloads } = useContext(ElectronDownloadContext);
  return (appId: string): boolean => !!appId && appId in downloads;
};
